import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import ProductCard from '@components/molecules/ProductCard'
import ProductFilter from '@components/molecules/ProductFilter'
import LoadMoreButton from '@components/molecules/LoadMoreButton'
import SortDropdown from '@components/molecules/SortDropdown'
import SelectedFilters from '@components/molecules/SelectedFilters'
import NoProductsFound from '@components/molecules/NoProductsFound'
import { ProductSimple } from '@interfaces/product'
import { ProductGridProps } from '@interfaces/productGrid'
import {
  filterProducts,
  sortProducts,
  updateUrl,
  parseUrlParams,
  handleFilterChange,
  handleClearAll,
  filterProductsBySearch,
} from '@helpers/filters'
import PromotionalCard from '@components/molecules/PromotionalCard'

const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  promotionalCard,
  settings,
  filters,
  sortBy,
  searchUrl,
  lensTechIcon
}) => {
  const { productsPerPage, enableAnimation, animationDuration, animationActivationTime, swatchAppearance, enableQuickAdd } = settings
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({})
  const [checkboxStates, setCheckboxStates] = useState<{ [key: string]: boolean }>({})
  const [sortValue, setSortValue] = useState<string>(sortBy.defaultValue)
  const [searchQuery, setSearchQuery] = useState('')
  const [productsToShow, setProductsToShow] = useState<number>(Number(productsPerPage))

  // Filter and sort products using useMemo
  const filteredProducts = useMemo(() => {
    if (!products) return []
    const filtered = filterProducts(products, selectedFilters, filters)
    return sortProducts(filtered, sortValue)
  }, [products, selectedFilters, filters, sortValue])

  const saveState = () => {
    sessionStorage.setItem('scrollPosition', String(window.scrollY));
    sessionStorage.setItem('productsToShow', String(productsToShow));
  };

  useEffect(() => {
    window.addEventListener('pagehide', saveState);
    window.addEventListener('popstate', saveState);

    return () => {
      window.removeEventListener('pagehide', saveState);
      window.removeEventListener('popstate', saveState);
    };
  }, [productsToShow]);

  // Update productsToShow when filteredProducts or productsPerPage changes or to retain scroll depth
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('scrollPosition');
    const savedProductsToShow = sessionStorage.getItem('productsToShow');
    const savedCollection = sessionStorage.getItem('lastViewedCollection');
    const currentCollection = window.location.pathname.split('/').pop() || "";
    const isSameCollection = savedCollection === currentCollection;

    // Update current collection
    sessionStorage.setItem('lastViewedCollection', currentCollection);

    if (isSameCollection) {
      setProductsToShow(Math.min(savedProductsToShow ? Number(savedProductsToShow) : Number(productsPerPage), filteredProducts.length));

      if (savedScrollPosition) {
        requestAnimationFrame(() => {
          window.scrollTo(0, Number(savedScrollPosition));
        });
      }
    } else {
      // Clear the state immediately before it gets used
      sessionStorage.removeItem('productsToShow');
      sessionStorage.removeItem('scrollPosition');
    }
  }, [filteredProducts, productsPerPage]);


  useEffect(() => {
    const { selectedFilters, checkboxStates, sortValue } = parseUrlParams(filters)
    setSelectedFilters(selectedFilters)
    setCheckboxStates(checkboxStates)
    setSortValue(sortValue || sortBy.defaultValue)
  }, [filters, sortBy.defaultValue])

  const handleFilterChangeWrapper = (filter: string, paramName: string) => {
    handleFilterChange(
      filter,
      paramName,
      selectedFilters,
      setSelectedFilters,
      setCheckboxStates,
      sortValue,
      filters,
      handleClearAllWrapper,
    )
  }

  const handleLoadMore = useCallback(() => {
    setProductsToShow((prev) => Math.min(prev + Number(productsPerPage), filteredProducts.length))
  }, [productsPerPage, filteredProducts.length])

  const handleSortChange = useCallback(
    (newSortValue: string) => {
      setSortValue(newSortValue)
      updateUrl(selectedFilters, newSortValue, filters, searchUrl)
    },
    [selectedFilters, filters, searchUrl],
  )

  const handleClearAllWrapper = useCallback(() => {
    handleClearAll(setSelectedFilters, setCheckboxStates, setSearchQuery, sortBy.defaultValue, filters, searchUrl)
  }, [sortBy.defaultValue, filters, searchUrl])

  const filteredProductsBySearch = useMemo(() => {
    return filterProductsBySearch(filteredProducts, searchQuery)
  }, [filteredProducts, searchQuery])

  const productCardsRef = useRef<HTMLDivElement>(null)
  const visibleCardsRef = useRef<HTMLElement[]>([])
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    // Only proceed if animation is enabled
    if (!enableAnimation) return

    // Function to shake a random visible card
    const shakeRandomVisibleCard = () => {
      if (visibleCardsRef.current.length > 0) {
        const randomIndex = Math.floor(Math.random() * visibleCardsRef.current.length)
        const cardToShake = visibleCardsRef.current[randomIndex] as HTMLElement
        cardToShake.classList.add('shake-animation')
        setTimeout(() => {
          cardToShake.classList.remove('shake-animation')
        }, animationDuration)
      }
      // Set another timeout to trigger the animation again after the specified time
      inactivityTimeoutRef.current = setTimeout(shakeRandomVisibleCard, animationActivationTime * 1000)
    }

    // Function to reset the inactivity timeout
    const resetInactivityTimeout = () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current)
      }
      inactivityTimeoutRef.current = setTimeout(shakeRandomVisibleCard, animationActivationTime * 1000)
    }

    // Function to handle user activity and reset the inactivity timeout
    const handleUserActivity = () => {
      resetInactivityTimeout()
    }

    // Add event listeners to track user activity
    window.addEventListener('mousemove', handleUserActivity)
    window.addEventListener('keydown', handleUserActivity)
    window.addEventListener('click', handleUserActivity)

    // Initialize the inactivity timeout
    resetInactivityTimeout()

    // Cleanup function to remove event listeners and clear the timeout
    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current)
      }
      window.removeEventListener('mousemove', handleUserActivity)
      window.removeEventListener('keydown', handleUserActivity)
      window.removeEventListener('click', handleUserActivity)
    }
  }, [enableAnimation, animationDuration, animationActivationTime, filteredProductsBySearch])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleCardsRef.current.push(entry.target as HTMLElement)
          } else {
            visibleCardsRef.current = visibleCardsRef.current.filter((card) => card !== entry.target)
          }
        })
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      },
    )
    if (productCardsRef.current) {
      const cards = productCardsRef.current.querySelectorAll('.product-card')
      cards.forEach((card) => observer.observe(card))
    }
    return () => observer.disconnect()
  }, [filteredProductsBySearch])

  return (
    <div className="container px-5 lg:px-16 lg:py-3">
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="min-w-72 lg:max-w-72">
          <ProductFilter
            filters={filters}
            onFilterChange={handleFilterChangeWrapper}
            checkboxStates={checkboxStates}
            handleClearAll={handleClearAllWrapper}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            swatchAppearance={swatchAppearance}
            productsToShow={productsToShow}
            totalProducts={filteredProductsBySearch.length}
            lensTechIcon={lensTechIcon}
          />
        </div>
        <div className="flex-1 flex flex-col">
          <div className="flex items-start justify-between gap-5 mb-6">
            <SelectedFilters selectedFilters={selectedFilters} onFilterChange={handleFilterChangeWrapper} />
            <SortDropdown sortBy={sortBy} onSortChange={handleSortChange} />
          </div>
          <div
            className="grid grid-cols-2 gap-2 pb-7.5 lg:grid-cols-2 lg:gap-x-2 lg:gap-y-6 xl:grid-cols-3"
            ref={productCardsRef}
          >
            {!!filteredProductsBySearch.length &&
              filteredProductsBySearch.slice(0, productsToShow).map((product: ProductSimple, index: number) => {
                const promosAtThisIndex = promotionalCard?.filter(
                  (promo) => promo.index === index
                )
                return (
                  <React.Fragment key={product.id}>
                    {promosAtThisIndex?.map((promoCard, i) =>(
                      <PromotionalCard key={`promo-${i}`} {...promoCard} />
                    ))}
                    <ProductCard
                      key={product.id}
                      product={product}
                      selectedFilters={selectedFilters}
                      className="product-card"
                      enableQuickAdd={enableQuickAdd}
                    />
                  </React.Fragment>
                )
              })}
          </div>
          {!filteredProductsBySearch.length && <NoProductsFound handleClearAll={handleClearAllWrapper} />}
          {filteredProductsBySearch.length > productsToShow && (
            <LoadMoreButton
              onLoadMore={handleLoadMore}
              productsToShow={productsToShow}
              totalProducts={filteredProductsBySearch.length}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default React.memo(ProductGrid)
